"use client";

import { createContext, useContext, Suspense } from "react";
import { useSessionInit } from "@/hooks/use-session-init";

interface AppContextType {
  region: "us" | "in" | "default";
  workflow: "og" | "ng";
}

const AppContext = createContext<AppContextType>({
  region: "default",
  workflow: "og",
});

// Inner component wrapped in Suspense
function AppProviderInner({
  children,
  region,
}: {
  children: React.ReactNode;
  region: "us" | "in" | "default";
  workflow: "og" | "ng";
}) {
  const workflowParam = "ng";

  // Initialize window.__inito__ with session_id
  useSessionInit();

  return (
    <AppContext.Provider value={{ region, workflow: workflowParam }}>
      {children}
    </AppContext.Provider>
  );
}

// Fallback provider when Suspense is pending
function AppProviderFallback({
  children,
  region,
  workflow,
}: {
  children: React.ReactNode;
  region: "us" | "in" | "default";
  workflow: "og" | "ng";
}) {
  return (
    <AppContext.Provider value={{ region, workflow }}>
      {children}
    </AppContext.Provider>
  );
}

export function AppProvider({
  children,
  region,
  workflow,
}: {
  children: React.ReactNode;
  region: "us" | "in" | "default";
  workflow: "og" | "ng";
}) {
  return (
    <Suspense
      fallback={
        <AppProviderFallback region={region} workflow={workflow}>
          {children}
        </AppProviderFallback>
      }
    >
      <AppProviderInner region={region} workflow={workflow}>
        {children}
      </AppProviderInner>
    </Suspense>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
